import React, { useEffect, useRef, useState } from "react";

import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Box, Button, Chip, Popover, Popper, Stack, TextField, Typography } from "@mui/material";
import { SelectAutocomplete } from "components/forms";
import { capitalize, startCase } from "lodash";
import { useAccountSelector, useDealActions } from "store";

const toCapitalizeList: Array<keyof Props["filtersList"]> = ["round", "deal_name"]; //added after capitalize .replace(/_/g," ") in case server changed key of filter Deal Name (replaced with deal_name)
interface Props {
  handleChangeFilter: (filters: Props["filtersList"]) => void;
  filtersList: Record<string, Array<number | string | boolean>>;
  applyMode?: boolean;
  state?: Props["filtersList"];
  beginningElement?: JSX.Element;
  refetchFilters?: (filters?: Props["filtersList"]) => void;
  isQuickView?: boolean;
  container?: Element | null;
  additionalFilters?: React.ReactNode;
  onClearAdditionalFilters?: () => void;
}

export const InlineFilter: React.FC<Props> = ({
  handleChangeFilter,
  filtersList,
  state = {},
  applyMode = false,
  beginningElement,
  refetchFilters,
  isQuickView = false,
  container,
  additionalFilters,
  onClearAdditionalFilters
}) => {
  const [openAddPopover, setOpenAddPopover] = useState(false);
  const [filterEdit, setFilterEdit] = useState<string>("");
  const [buttonsOpen, setButtonsOpen] = useState(false);
  const { setMeasureDataTableFiltersAction: setDataFilters } = useDealActions();
  const { show_global_investor_filter } = useAccountSelector() || {};

  //filters - object {key: value} that filter used for data load (state)
  const [filters, setFilters] = useState<Props["filtersList"]>(state || {});

  //selectedValues - array of values that we selected  and after save apply to filters
  const [selectedValues, setSelectedValues] = useState<Props["filtersList"][keyof Props["filtersList"]]>([]);

  //selectedFilters - array of keys that we selected and after save apply to filters
  const [selectedFilters, setSelectedFilters] = useState<string[]>(Object.keys(filters));

  //pendingFilters - (used for apply mode) object {key: value} that we selected and after save apply to filters
  const [pendingFilters, setPendingFilters] = useState<Props["filtersList"]>({});

  const anchorRef = useRef<HTMLElement>();
  const addRef = useRef<HTMLButtonElement>(null);
  const filtersNumber = Object.keys(filters).length;
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  const handleClose = () => {
    setFilterEdit("");
    setSelectedValues([]);
  };

  const handleCloseAddFilters = () => {
    setOpenAddPopover(false);
  };

  const handleSet = () => {
    const copy = { ...filters };

    selectedValues ? (copy[filterEdit] = selectedValues) : delete copy[filterEdit];
    if (applyMode) {
      refetchFilters?.(copy);
    }
    setFilters(copy);
    if (!applyMode) {
      handleChangeFilter(copy);
    } else {
      setPendingFilters(copy);
      setButtonsOpen(true);
    }
  };

  const handleAddFilters = () => {
    const copy = { ...filters };

    // Remove filters if they were unselected
    Object.keys(copy).forEach((filter) => {
      if (selectedFilters.includes(filter)) return;
      delete copy[filter];
    });

    // Copy selected filter to newly selected
    selectedFilters.forEach((filter) => {
      copy[filter] = copy[filter] || [];
    });

    setFilters(copy);
    if (!applyMode) {
      setSelectedFilters(Object.keys(copy));
    } else {
      setPendingFilters(copy);
      setButtonsOpen(true);
    }
    handleCloseAddFilters();
  };

  const removeFilter = (filter: string): void => {
    refetchFilters?.();
    const filtersCopy = { ...filters };
    delete filtersCopy[filter];
    setFilters(filtersCopy);
    if (!applyMode) {
      setSelectedFilters((prev) => prev.filter((f) => f !== filter));
      handleChangeFilter(filtersCopy);
    } else {
      setPendingFilters(filtersCopy);
      setSelectedFilters((prev) => prev.filter((f) => f !== filter));
      setButtonsOpen(true);
    }
  };

  const openFilter = (e: React.MouseEvent, filter: string) => {
    anchorRef.current = e.target as HTMLElement;
    setFilterEdit(filter);
    setSelectedValues(filters[filter]);
  };

  const handleClearFilters = () => {
    refetchFilters?.();
    setFilters({});
    setSelectedFilters([]);
    if (onClearAdditionalFilters) {
      onClearAdditionalFilters();
      if (applyMode) {
        setPendingFilters({});
        setButtonsOpen(false);
      }
    } else if (!applyMode) {
      handleChangeFilter({});
    } else {
      setPendingFilters({});
      handleChangeFilter({});
      setButtonsOpen(false);
    }
    if (show_global_investor_filter) {
      setDataFilters({ investors: [] });
    }
  };

  const handleApply = () => {
    handleChangeFilter(pendingFilters);
    setButtonsOpen(false);
    if (show_global_investor_filter && !pendingFilters?.Investor) {
      setDataFilters({ investors: [] });
    }
  };

  const delayedFocus = () => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 500);
  };

  useEffect(() => {
    if (openAddPopover) {
      delayedFocus();
    }
    if (filterEdit) {
      delayedFocus();
    }
  }, [openAddPopover, filterEdit]);

  useEffect(() => {
    if (!filterEdit) return;
    setSelectedValues(filters[filterEdit]);
  }, [filterEdit]);

  //set filters from state, if user select some bookmark we need set filters from state
  useEffect(() => {
    // if (!Object.keys(state).length) return;
    setFilters(state);
    setSelectedFilters(Object.keys(state));
  }, [state]);

  return (
    <>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", gap: "15px" }}>
        <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
          {beginningElement}
          <Button
            ref={addRef}
            variant="outlined"
            color="secondary"
            size="sm"
            sx={{ borderColor: "#dae0e2" }}
            onClick={() => setOpenAddPopover(true)}
            startIcon={<FilterAltOutlinedIcon sx={{ fontSize: "1.2rem", marginTop: "-3px" }} />}
          >
            Filters ({filtersNumber})
          </Button>
          {Object.entries(filters).map(([filter, values]) => (
            <Chip
              size="small"
              key={filter}
              sx={{ height: "26px", fontSize: "15px", color: "#0a3142" }}
              label={`${toCapitalizeList.includes(filter) ? (filter === "deal_name" ? "Deal Name" : capitalize(filter).replace(/_/g, " ")) : filter} (${
                values.length
              })`}
              onDelete={() => removeFilter(filter)}
              onClick={(e) => openFilter(e, filter)}
            />
          ))}
          {additionalFilters ?? null}
          {(Boolean(selectedFilters.length) || Boolean(additionalFilters)) && (
            <Button size="sm" sx={{ color: "#26A6AE" }} onClick={handleClearFilters}>
              Clear All
            </Button>
          )}
          {buttonsOpen && applyMode && (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Button variant="contained" onClick={handleApply} color="primary">
                  Apply
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Popover
        open={openAddPopover}
        anchorEl={addRef!.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        onClose={() => {
          setSelectedFilters(Object.keys(filters));
          handleCloseAddFilters();
        }}
        disableEnforceFocus
        disableAutoFocus
        container={container ?? document.body}
      >
        <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
          <Stack
            sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: "15px" }}>Add Filter</Typography>
            <Stack flexDirection="row">
              <Button
                sx={{ color: "#26A6AE" }}
                variant="text"
                onClick={() => {
                  setSelectedFilters(Object.keys(filters));
                  handleCloseAddFilters();
                }}
              >
                Cancel
              </Button>
              <Button
                sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
                variant="text"
                onClick={handleAddFilters}
              >
                Save
              </Button>
            </Stack>
          </Stack>
          {filtersList && (
            <SelectAutocomplete<string, true>
              multiple
              disableCloseOnSelect
              selectAll
              openOnFocus
              limitTags={1}
              PopperComponent={(props) => (
                <Popper
                  {...props}
                  container={container ?? document.body}
                  sx={{
                    "& .MuiAutocomplete-option": {
                      "&:hover": {
                        backgroundColor: "#edf1f9 !important"
                      }
                    }
                  }}
                />
              )}
              options={Object.keys(filtersList).map((item) => {
                if (item === "deal_name" && !isQuickView) {
                  return "Deal Name";
                }
                return item;
              })}
              renderInput={(params) => <TextField {...params} label="Filters" inputRef={inputRef} />}
              renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option) => (
                <li {...props} key={option}>
                  {toCapitalizeList.includes(option) ? startCase(option) : option}
                </li>
              )}
              renderTags={(tagValue, getTagProps) => {
                return (
                  <div style={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
                    {tagValue.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={index}
                        label={toCapitalizeList?.includes(option) ? capitalize(option).replace(/_/g, " ") : option}
                      />
                    ))}
                  </div>
                );
              }}
              sx={{ maxWidth: "300px", minWidth: "300px" }}
              value={selectedFilters.map((item) => {
                if (item === "deal_name") {
                  return "Deal Name";
                }
                return item;
              })}
              setValue={(value) => {
                setSelectedFilters(
                  value.map((item) => {
                    if (item === "Deal Name" && !isQuickView) {
                      return "deal_name";
                    }
                    return item;
                  })
                );
              }}
            />
          )}
        </Stack>
      </Popover>
      <Popover
        open={!!filterEdit}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        onClose={() => {
          handleClose();
          setButtonsOpen(false);
        }}
        disableEnforceFocus
        disableAutoFocus
        container={container ?? document.body}
      >
        <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
          <Stack
            sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: "15px" }}>
              {toCapitalizeList.includes(filterEdit) ? capitalize(filterEdit).replace(/_/g, " ") : filterEdit}
            </Typography>
            <Stack flexDirection="row">
              <Button
                sx={{ color: "#26A6AE" }}
                variant="text"
                onClick={() => {
                  handleClose();
                  setButtonsOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
                variant="text"
                onClick={() => {
                  handleSet();
                  handleClose();
                }}
              >
                Save
              </Button>
            </Stack>
          </Stack>
          <SelectAutocomplete<string, true>
            multiple
            disableCloseOnSelect
            selectAll
            limitTags={1}
            openOnFocus
            PopperComponent={(props) => (
              <Popper
                {...props}
                container={container ?? document.body}
                sx={{
                  "& .MuiAutocomplete-option": {
                    "&:hover": {
                      backgroundColor: "#edf1f9 !important"
                    }
                  }
                }}
              />
            )}
            options={filtersList?.[filterEdit]?.map(String) || []}
            renderInput={(params) => <TextField {...params} label="Filters" inputRef={inputRef} />}
            renderTags={(tagValue, getTagProps) => {
              return (
                <div style={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
                  {tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={toCapitalizeList?.includes(option) ? capitalize(option).replace(/_/g, " ") : option}
                    />
                  ))}
                </div>
              );
            }}
            sx={{ maxWidth: "300px", minWidth: "300px" }}
            value={(selectedValues as Array<string>) || []}
            setValue={(value) => setSelectedValues(value as Array<string>)}
          />
        </Stack>
      </Popover>
    </>
  );
};
